/** Full-role palettes belong to the reusable piece system, not a particular biome. */
import type { ResolvedPalette } from './contract';
import { LEGACY_PIECE_PALETTES } from './legacyPalettes';
import { MILLRACE_PALETTES } from './millracePalettes';

export function createPiecePaletteRegistry<T extends Record<string, ResolvedPalette>>(entries: T): Readonly<T> {
  const roles = Object.keys(LEGACY_PIECE_PALETTES.coastal_salt) as Array<keyof ResolvedPalette>;
  const result: Record<string, ResolvedPalette> = Object.create(null);
  for (const [id, palette] of Object.entries(entries)) {
    if (!/^[a-z][a-z0-9_]*$/.test(id)) throw new Error(`Invalid piece palette id: ${id}`);
    if (!palette || Object.keys(palette).length !== roles.length
      || roles.some(role => typeof palette[role] !== 'string' || !/^#[0-9a-f]{6}$/i.test(palette[role]))) {
      throw new Error(`${id}: every piece palette role needs a six-digit hex colour`);
    }
    result[id] = Object.freeze({ ...palette });
  }
  return Object.freeze(result) as Readonly<T>;
}

export const PIECE_PALETTES = createPiecePaletteRegistry({ ...LEGACY_PIECE_PALETTES, ...MILLRACE_PALETTES });
export type PiecePaletteId = keyof typeof PIECE_PALETTES;
export function isPiecePaletteId(id: unknown): id is PiecePaletteId {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(PIECE_PALETTES, id);
}
export function piecePalette(id: string, registry: Readonly<Record<string, ResolvedPalette>> = PIECE_PALETTES): ResolvedPalette {
  if (!Object.prototype.hasOwnProperty.call(registry, id)) throw new Error(`Unknown piece palette: ${id}`);
  return registry[id];
}
